import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table/table";
import { Input } from "@/components/ui/input/input";
import { formatDuration } from "@/components/break/format";
import { getTeamBreakTotalsQueryOptions } from "@/config/queries/teamleader/tl-query-options";

const istDay = (d: Date) => new Date(d.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

export default function TeamLeaderSummaryPage() {
  const key = Cookies.get("key") as string;
  const [from, setFrom] = useState(istDay(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(istDay(new Date()));

  const start = useMemo(() => new Date(`${from}T12:00:00+05:30`), [from]);
  const end = useMemo(() => new Date(`${to}T12:00:00+05:30`), [to]);
  const { data, isFetching } = useQuery(getTeamBreakTotalsQueryOptions(key, start, end));
  const rows = data?.employees ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium">Break summary</h1>
          <p className="text-sm text-muted-foreground">
            Ranked by total break time. Break time is never deducted from working hours.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-[160px]" data-testid="tl-from" />
          <span className="text-muted-foreground">to</span>
          <Input type="date" value={to} max={istDay(new Date())} onChange={(e) => setTo(e.target.value)} className="w-[160px]" data-testid="tl-to" />
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table data-testid="tl-summary-table">
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Breaks</TableHead>
              <TableHead>Total break time</TableHead>
              <TableHead>Average break</TableHead>
              <TableHead>Days with breaks</TableHead>
              <TableHead>Approved leave days</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {isFetching ? "Loading…" : "No breaks recorded in this range."}
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.employee._id} data-testid="tl-summary-row">
                <TableCell className="font-medium">{r.employee.name}</TableCell>
                <TableCell>{r.breakCount}</TableCell>
                <TableCell className="font-medium">{formatDuration(r.totalBreakSeconds)}</TableCell>
                <TableCell>{formatDuration(r.averageBreakSeconds)}</TableCell>
                <TableCell>{r.daysWithBreaks}</TableCell>
                <TableCell>{r.approvedLeaveDays || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
